/**
 * Issue reporter: automatic error capture plus the "Report a problem" send.
 *
 * Shared by the display, upload and moderation apps. The three copies are
 * identical: change one, copy it to the other two. Everything posts
 * same-origin to /api/report-issue, which CloudFront routes to the issue
 * log Lambda (infrastructure/lambda/report-issue).
 *
 * Privacy: we send the page path (never the query string or hash), the
 * browser, the screen size, the error and a short trail of recent page
 * changes and API calls (method, path, status). Never form contents, file
 * names or anything the person typed, except the report message itself.
 */

export type IssueApp = 'display' | 'upload' | 'moderation';

export interface Breadcrumb {
  at: string;
  type: 'nav' | 'api' | 'action';
  message: string;
}

export const ISSUE_ENDPOINT = '/api/report-issue';

const MAX_BREADCRUMBS = 20;
/** Automatic error sends per page load. A render loop must not spam. */
const MAX_AUTO_REPORTS = 5;

const REF_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

interface State {
  app: IssueApp;
  release?: string;
  breadcrumbs: Breadcrumb[];
  autoSent: number;
  seen: Set<string>;
  lastErrorRef?: string;
  installed: boolean;
}

let state: State | null = null;

/** Short reference people can quote back, e.g. BPF-7KQ2MD. */
export function newRef(): string {
  const bytes = new Uint8Array(6);
  crypto.getRandomValues(bytes);
  let out = 'BPF-';
  for (const b of bytes) out += REF_ALPHABET[b % REF_ALPHABET.length];
  return out;
}

/** Origin + path only. Query strings and fragments can hold tokens. */
export function scrubUrl(url: string): string {
  try {
    const u = new URL(url, typeof location !== 'undefined' ? location.href : undefined);
    return `${u.origin}${u.pathname}`;
  } catch {
    return '';
  }
}

function pathOnly(url: string): string {
  try {
    return new URL(url, location.href).pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
}

export function addBreadcrumb(type: Breadcrumb['type'], message: string): void {
  if (!state) return;
  state.breadcrumbs.push({ at: new Date().toISOString(), type, message: message.slice(0, 300) });
  if (state.breadcrumbs.length > MAX_BREADCRUMBS) state.breadcrumbs.shift();
}

/** Record an API call outcome. Path only, never the body. */
export function recordApiCall(method: string, url: string, status: number | 'network-error'): void {
  addBreadcrumb('api', `${method.toUpperCase()} ${pathOnly(url)} ${status}`);
}

/** Reference of the most recent automatic error, to link a report to it. */
export function lastErrorRef(): string | undefined {
  return state?.lastErrorRef;
}

function environment() {
  return {
    page: scrubUrl(location.href),
    userAgent: navigator.userAgent,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    language: navigator.language,
  };
}

function post(payload: Record<string, unknown>): Promise<Response> {
  return fetch(ISSUE_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    // Lets the send finish when the error is followed by a navigation.
    keepalive: true,
  });
}

function toErrorParts(error: unknown): { name?: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message || String(error), stack: error.stack };
  }
  if (typeof error === 'string') return { message: error };
  try {
    return { message: JSON.stringify(error) };
  } catch {
    return { message: String(error) };
  }
}

/**
 * Report an error automatically. Returns the reference so an error screen
 * can show it and offer "Tell us what happened". Never throws.
 */
export function reportError(error: unknown, context?: Record<string, string | number | boolean>): string | undefined {
  if (!state) return undefined;
  const parts = toErrorParts(error);
  // Browser-extension and cross-origin script noise carries no detail.
  if (!parts.message || parts.message === 'Script error.') return undefined;

  const key = `${parts.name}|${parts.message}`;
  if (state.seen.has(key)) return state.lastErrorRef;
  if (state.autoSent >= MAX_AUTO_REPORTS) return undefined;
  state.seen.add(key);
  state.autoSent += 1;

  const ref = newRef();
  state.lastErrorRef = ref;
  try {
    void post({
      kind: 'error',
      app: state.app,
      ref,
      release: state.release,
      ...environment(),
      error: parts,
      context,
      breadcrumbs: state.breadcrumbs,
    }).catch(() => undefined);
  } catch {
    // Reporting must never cause a second error.
  }
  return ref;
}

export interface ProblemReport {
  message: string;
  email?: string;
  relatedRef?: string;
  /** Hidden honeypot field value. */
  website?: string;
}

/** Send a "Report a problem" message. Resolves with the reference. */
export async function sendProblemReport(report: ProblemReport): Promise<string> {
  const ref = newRef();
  const res = await post({
    kind: 'report',
    app: state?.app ?? 'display',
    ref,
    release: state?.release,
    ...environment(),
    message: report.message,
    email: report.email || undefined,
    relatedRef: report.relatedRef || undefined,
    website: report.website || undefined,
    breadcrumbs: state?.breadcrumbs ?? [],
  });
  if (!res.ok) throw new Error(`Report failed with status ${res.status}`);
  const body = (await res.json().catch(() => ({}))) as { ref?: string };
  return body.ref || ref;
}

/**
 * Install global capture once per page: uncaught errors, unhandled promise
 * rejections and page changes (for the breadcrumb trail).
 */
export function initIssueReporter(options: { app: IssueApp; release?: string }): void {
  if (typeof window === 'undefined') return;
  if (state?.installed) return;
  state = {
    app: options.app,
    release: options.release,
    breadcrumbs: [],
    autoSent: 0,
    seen: new Set(),
    installed: true,
  };

  window.addEventListener('error', (event) => {
    reportError(event.error ?? event.message, {
      source: event.filename ? scrubUrl(event.filename) : '',
      line: event.lineno ?? 0,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason ?? 'Unhandled promise rejection', { type: 'unhandledrejection' });
  });

  const nav = () => addBreadcrumb('nav', `${location.pathname}${location.hash.split('?')[0]}`);
  nav();
  window.addEventListener('popstate', nav);
  window.addEventListener('hashchange', nav);
}

/** Test hook. */
export function resetIssueReporterForTesting(): void {
  state = null;
}
